import { RecaptchaContext } from '@quiz-frontend/quiz-builder/components/common/recaptcha';
import Head from 'next/head';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Form, FormApiInterface, Validator } from 'react-uforms';

import { nextRedirect, toCapitalize } from '../service/helpers';
import { validateEmail } from '../service/validators/validateEmail';
import { triggerRecaptcha } from '../store/app/thunks';
import { completeQuiz, saveAnswers, toggleQuestions } from '../store/quiz/actions';
import {
  answeredQuestionIndexSelector,
  currentQuestionsSelector,
  initialValuesSelector,
  stepImageSelector,
} from '../store/quiz/selectors';
import { showFooterMessage } from '../store/quiz/thunks';
import { QuizValues, Step, StepLayouts } from '../store/quiz/types';
import { RootState } from '../store/root-reducer';
import { Question, ThunkActionDispatch } from '../types';
import ImagesPreloader from './common/images-preloader';
import Footer from './footer';
import ComplexStepLayout from './steps/complex.layout';
import FormStepLayout from './steps/form.layout';
import RowsStepLayout from './steps/rows.layout';
import SimpleStepLayout from './steps/simple.layout';

class Wizard extends React.Component<{
  currentStep: Step;
  nextStepSlug?: string;
  currentQuestions: Question[];
  initialValues: QuizValues;
  answers: QuizValues;
  image?: string;
  answeredQuestionIndex: number | null;
  currentQuestionNumber: number | null;
  saveAnswers: typeof saveAnswers;
  toggleQuestions: typeof toggleQuestions;
  showFooterMessage: ThunkActionDispatch<typeof showFooterMessage>;
  triggerRecaptcha: ThunkActionDispatch<typeof triggerRecaptcha>;
  completeQuiz: typeof completeQuiz;
  onComplete?: () => void;
}> {
  static contextType = RecaptchaContext;
  context: React.ContextType<typeof RecaptchaContext>;

  getValidationRules = () => {
    const { currentQuestions } = this.props;
    return currentQuestions.reduce((acc, question) => {
      acc[question.name] = [];
      const fieldValidators = acc[question.name];

      if (question.required) {
        const fieldName = question.name
          .split(/(?=[A-Z])/)
          .map((el) => toCapitalize(el))
          .join(' ');
        fieldValidators.push(Validator.Required(`${fieldName} is required`));
      }

      if (question.minAnswers) {
        fieldValidators.push((value: unknown[]) => {
          if (value.length < question.minAnswers) {
            return `Select at least ${question.minAnswers} answer(s)`;
          }
          return true;
        });
      }
      if (question.maxAnswers) {
        fieldValidators.push((value: unknown[]) => {
          if (value.length > question.maxAnswers) {
            return `Select no more than ${question.maxAnswers} answer(s)`;
          }
          return true;
        });
      }

      if (question.type === 'email') {
        fieldValidators.push((value: unknown[]) => {
          if (!validateEmail(value)) {
            return 'Emails is invalid';
          }
          return true;
        });
      }

      if (question.type === 'text') {
        fieldValidators.push(Validator.MinLength(2, 'Field must be greater than 2 characters'));
        fieldValidators.push(Validator.MaxLength(64, 'Field  must be no more than 64 characters'));
      }

      return acc;
    }, {});
  };

  handleSubmit = async (values: QuizValues) => {
    const { saveAnswers, triggerRecaptcha, onComplete, nextStepSlug, completeQuiz } = this.props;
    saveAnswers(values);

    if (!nextStepSlug) {
      await triggerRecaptcha(this.context);
      if (onComplete) {
        await onComplete();
      }
      completeQuiz();
    } else {
      nextRedirect(null, '/', undefined, { scrollTop: true, query: { step: nextStepSlug } });
    }
  };

  handleChange = (api: FormApiInterface) => {
    const { showFooterMessage } = this.props;
    showFooterMessage(api.getAllValues());
  };

  render() {
    const {
      image,
      currentStep,
      initialValues,
      currentQuestions,
      toggleQuestions,
      answers,
      answeredQuestionIndex,
      currentQuestionNumber,
    } = this.props;
    if (!currentStep) {
      return null;
    }

    const getStepLayout = (layout: StepLayouts) => {
      switch (layout) {
        case StepLayouts.COMPLEX:
          return (
            <ComplexStepLayout
              question={currentQuestions[0]}
              title={currentStep.title}
              onToggle={toggleQuestions}
              image={image}
              answeredQuestion={answeredQuestionIndex}
            />
          );
        case StepLayouts.SIMPLE:
          return <SimpleStepLayout question={currentQuestions[0]} title={currentStep.title} image={image} />;
        case StepLayouts.FORM:
          return <FormStepLayout questions={currentQuestions} title={currentStep.title} image={image} />;
        case StepLayouts.ROWS:
          return <RowsStepLayout question={currentQuestions[0]} title={currentStep.title} />;
      }
    };
    return (
      <Fragment>
        <Head>
          <title>{currentStep.title}</title>
        </Head>
        <ImagesPreloader />
        <Form
          key={`${currentStep.slug}_${currentQuestionNumber}`}
          defaultValues={{ ...initialValues, ...answers }}
          validation={this.getValidationRules}
          onChange={this.handleChange}
          onSubmit={this.handleSubmit}
        >
          {({ getAllValues, getAllErrors }) => (
            <Fragment>
              {getStepLayout(currentStep.layout)}
              <Footer
                errors={{ ...getAllErrors() }}
                values={{ ...getAllValues() }}
                currentQuestions={currentQuestions}
              />
            </Fragment>
          )}
        </Form>
      </Fragment>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  return {
    loading: state.quizState.loading,
    currentStep: state.quizState.currentStep,
    nextStepSlug: state.quizState.nextStepSlug,
    currentQuestionNumber: state.quizState.currentQuestionNumber,
    image: stepImageSelector(state),
    answers: state.quizState.answers,
    answeredQuestionIndex: answeredQuestionIndexSelector(state),
    currentQuestions: currentQuestionsSelector(state),
    initialValues: initialValuesSelector(state),
  };
};

export default connect(mapStateToProps, {
  saveAnswers,
  toggleQuestions,
  triggerRecaptcha,
  showFooterMessage,
  completeQuiz,
})(Wizard);
